import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Pencil, Trash2, Search, Users, CheckCircle2, AlertTriangle, XCircle, UserPlus, Plus, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatCard } from "../components/StatCard";
import { PageHeader, card, btnPrimary } from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const NEW_FIELDS = [["first_name", "İsim"], ["last_name", "Soyisim"], ["phone", "Telefon"], ["username", "Kullanıcı Adı"], ["password", "Şifre", "password"], ["license_days", "Lisans Süresi (gün)", "number"]];

function CreateDialog({ open, onClose, onSaved }) {
  const [f, setF] = useState({ license_days: 30 });
  const save = async () => {
    const days = parseInt(f.license_days, 10);
    if (!days || days < 1) return toast.error("Lisans süresi en az 1 gün olmalı");
    try {
      await api.post("/admin/users", { ...f, license_days: days });
      toast.success("Kullanıcı oluşturuldu");
      setF({ license_days: 30 });
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="admin-create-dialog">
        <DialogHeader><DialogTitle className="font-heading">Yeni Kullanıcı</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {NEW_FIELDS.map(([k, l, t]) => (
            <div key={k}>
              <Label className="text-xs">{l}</Label>
              <Input data-testid={`admin-create-${k}`} type={t || "text"} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 h-10 rounded-xl" />
            </div>
          ))}
        </div>
        <button data-testid="admin-create-save-button" onClick={save} className={btnPrimary}>Oluştur</button>
      </DialogContent>
    </Dialog>
  );
}

function UserBusinesses({ uid }) {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("isletme");
  const load = useCallback(async () => setList((await api.get(`/admin/users/${uid}/businesses`)).data), [uid]);
  useEffect(() => {
    load();
  }, [load]);
  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 p-3" data-testid="admin-user-businesses">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">İşletmeler / Şahıslar</div>
      {list.map((b) => (
        <div key={b.id} className="flex items-center gap-2 text-sm" data-testid={`admin-business-${b.id}`}>
          <Building2 className="h-4 w-4 text-cyan-700" />
          <span className="flex-1 truncate">{b.name} <span className="text-xs text-slate-400">· {b.kind === "sahis" ? "Şahıs" : "İşletme"}</span></span>
          <button data-testid={`admin-business-delete-${b.id}`} onClick={() => window.confirm(`"${b.name}" ve tüm kayıtları silinsin mi?`) && run(() => api.delete(`/admin/businesses/${b.id}`), "Silindi")}
            className="rounded p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <Input data-testid="admin-business-name-input" placeholder="Yeni işletme adı" value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-lg" />
        <select data-testid="admin-business-kind-select" value={kind} onChange={(e) => setKind(e.target.value)} className="h-9 rounded-lg border border-slate-200 px-2 text-sm">
          <option value="isletme">İşletme</option>
          <option value="sahis">Şahıs</option>
        </select>
        <button data-testid="admin-business-add-button" onClick={() => name.trim() && run(() => api.post(`/admin/users/${uid}/businesses`, { name, kind }).then(() => setName("")), "Eklendi")}
          className="rounded-lg bg-cyan-700 px-2 text-white"><Plus className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function EditDialog({ u, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => {
    if (u) setF({ first_name: u.first_name, last_name: u.last_name, phone: u.phone, license_end: new Date(u.license_end).toLocaleDateString("en-CA"), password: "" });
  }, [u]);
  const save = async () => {
    try {
      await api.put(`/admin/users/${u.id}`, { ...f, password: f.password || null });
      toast.success("Üyelik güncellendi");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const fields = [["first_name", "İsim"], ["last_name", "Soyisim"], ["phone", "Telefon"], ["license_end", "Lisans Bitiş Tarihi", "date"], ["password", "Yeni Şifre (opsiyonel)", "password"]];
  return (
    <Dialog open={!!u} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto rounded-2xl" data-testid="admin-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading">@{u?.username} düzenle</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          {fields.map(([k, l, t]) => (
            <div key={k}>
              <Label className="text-xs">{l}</Label>
              <Input data-testid={`admin-edit-${k}`} type={t || "text"} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 h-10 rounded-xl" />
            </div>
          ))}
        </div>
        {u && <UserBusinesses uid={u.id} />}
        <button data-testid="admin-edit-save-button" onClick={save} className={btnPrimary}>Kaydet</button>
      </DialogContent>
    </Dialog>
  );
}

function DaysBadge({ u }) {
  if (u.role === "superadmin") return <span className="rounded-full bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white">Süresiz</span>;
  const cls = !u.license_active ? "bg-rose-100 text-rose-700" : u.days_left <= 30 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700";
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}>{u.license_active ? `${u.days_left} gün` : "Doldu"}</span>;
}

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const [u, s] = await Promise.all([api.get("/admin/users"), api.get("/admin/stats")]);
    setUsers(u.data);
    setStats(s.data);
  }, []);
  useEffect(() => {
    if (user.role === "superadmin") load();
  }, [load, user.role]);

  if (user.role !== "superadmin") return <Navigate to="/" replace />;

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const extend = (u, days) => act(() => api.post(`/admin/users/${u.id}/extend`, { days }), "Lisans uzatıldı");
  const toggle = (u, active) => act(() => api.put(`/admin/users/${u.id}`, { active }), active ? "Aktifleştirildi" : "Pasifleştirildi");
  const del = (u) => window.confirm(`@${u.username} ve tüm verileri silinsin mi?`) && act(() => api.delete(`/admin/users/${u.id}`), "Kullanıcı silindi");

  const list = users.filter((u) => `${u.username} ${u.first_name} ${u.last_name} ${u.phone}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="admin-page">
      <PageHeader title="SüperAdmin Paneli" subtitle="Üyelikleri, işletmeleri ve süreli lisansları yönetin.">
        <button data-testid="admin-create-user-button" onClick={() => setCreating(true)} className={btnPrimary}><UserPlus className="h-4 w-4" /> Yeni Kullanıcı</button>
      </PageHeader>
      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard testId="admin-stat-total" label="Toplam Üye" value={stats.total} icon={Users} tone="navy" />
          <StatCard testId="admin-stat-active" label="Aktif Lisans" value={stats.active_license} icon={CheckCircle2} tone="green" />
          <StatCard testId="admin-stat-expiring" label="30 Gün İçinde Bitecek" value={stats.expiring} icon={AlertTriangle} tone="blue" />
          <StatCard testId="admin-stat-expired" label="Süresi Dolan" value={stats.expired} icon={XCircle} tone="red" />
        </div>
      )}
      <div className={card}>
        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input data-testid="admin-search-input" placeholder="Kullanıcı ara…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 rounded-xl pl-9" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm" data-testid="admin-users-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="py-2 pr-3">Kullanıcı</th><th className="py-2 pr-3">Telefon</th><th className="py-2 pr-3">Kayıt</th><th className="py-2 pr-3">Lisans Bitiş</th>
                <th className="py-2 pr-3">Kalan</th><th className="py-2 pr-3">İşletme</th><th className="py-2 pr-3">Aktif</th><th className="py-2 text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {list.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-user-row-${u.username}`}>
                  <td className="py-3 pr-3"><div className="font-semibold text-slate-900">{u.first_name} {u.last_name}</div><div className="text-xs text-slate-500">@{u.username}</div></td>
                  <td className="py-3 pr-3">{u.phone || "—"}</td>
                  <td className="py-3 pr-3">{new Date(u.created_at).toLocaleDateString("tr-TR")}</td>
                  <td className="py-3 pr-3 font-mono">{new Date(u.license_end).toLocaleDateString("tr-TR")}</td>
                  <td className="py-3 pr-3"><DaysBadge u={u} /></td>
                  <td className="py-3 pr-3">{u.business_count}</td>
                  <td className="py-3 pr-3">{u.role !== "superadmin" && <Switch data-testid={`admin-active-switch-${u.username}`} checked={u.active} onCheckedChange={(v) => toggle(u, v)} />}</td>
                  <td className="py-3">
                    {u.role === "superadmin" ? (
                      <div className="flex justify-end">
                        <button data-testid={`admin-edit-${u.username}`} onClick={() => setEdit(u)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-1">
                        {[[30, "+1 Ay"], [90, "+3 Ay"], [365, "+1 Yıl"]].map(([d, l]) => (
                          <button key={d} data-testid={`admin-extend-${d}-${u.username}`} onClick={() => extend(u, d)} className="rounded-lg border border-cyan-200 bg-cyan-50 px-2 py-1 text-xs font-semibold text-cyan-800 hover:bg-cyan-100">{l}</button>
                        ))}
                        <button data-testid={`admin-edit-${u.username}`} onClick={() => setEdit(u)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                        <button data-testid={`admin-delete-${u.username}`} onClick={() => del(u)} className="rounded-lg p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <EditDialog u={edit} onClose={() => { setEdit(null); load(); }} onSaved={() => { setEdit(null); load(); }} />
      <CreateDialog open={creating} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
    </div>
  );
}
